import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Trash2, Check, AlertTriangle, Loader2, Download, Users, Package, ChevronDown, ChevronRight, X, RefreshCw } from 'lucide-react';
import { productAPI, bulkImportAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';

const BulkImportTab = () => {
  const [mode, setMode] = useState(null); // 'products' or 'contacts'
  const [step, setStep] = useState('upload'); // upload, preview, importing, done
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteFirst, setDeleteFirst] = useState(false);
  const [error, setError] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);
  const [tallyImporting, setTallyImporting] = useState(false);
  const fileRef = useRef(null);

  const handleImportTally = async () => {
    if (!window.confirm('Import vendors from Tally creditors? Existing vendors will be skipped.')) return;
    setTallyImporting(true);
    try {
      const r = await api.post('/vendors/import-from-tally');
      toast.success(`Imported ${r.data.imported} vendors (${r.data.skipped} skipped as duplicates)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Tally import failed. Ensure Tally ODBC server is running.'); }
    finally { setTallyImporting(false); }
  };

  const reset = () => {
    setMode(null);
    setStep('upload');
    setPreviewData(null);
    setResult(null);
    setError('');
    setDeleteFirst(false);
    setExpandedSection(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      let data;
      if (mode === 'products') {
        data = await productAPI.bulkImportPreview(file);
      } else {
        data = await bulkImportAPI.previewContacts(file);
      }
      setPreviewData(data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewData) return;
    setStep('importing');
    setLoading(true);
    setError('');
    try {
      // Delete existing data first if checked
      if (deleteFirst) {
        if (mode === 'products') {
          await bulkImportAPI.deleteAllProducts();
        } else {
          await bulkImportAPI.deleteAllContacts();
        }
      }

      let res;
      if (mode === 'products') {
        res = await productAPI.bulkImportConfirm({ items: previewData.items.filter(i => !i.is_duplicate || deleteFirst) });
      } else {
        const itemsToImport = previewData.items.filter(i => {
          if (deleteFirst) return true;
          if (i.type === 'Buyer' || i.type === 'Both') {
            if (i.is_existing_customer && !deleteFirst) return false;
          }
          if (i.type === 'Supplier' || i.type === 'Both') {
            if (i.is_existing_vendor && !deleteFirst) return false;
          }
          return true;
        });
        res = await bulkImportAPI.confirmContacts({ items: itemsToImport });
      }
      setResult(res);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  // --- Upload Step ---
  if (!mode) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Bulk Import</h3>
          <p className="text-xs text-gray-500 mb-6">Upload Excel files to import master data into the ERP system</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setMode('products')}
              data-testid="import-mode-products"
              className="group p-6 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-400 hover:bg-blue-50/30 transition-all text-left"
            >
              <Package className="w-8 h-8 text-blue-600 mb-3" />
              <h4 className="text-sm font-bold text-gray-900">Item Master / Products</h4>
              <p className="text-[11px] text-gray-500 mt-1">Import products with SKU, Description, Category, UOM, HSN, GST, Cost/Selling Price, MIN/MAX stock levels</p>
              <p className="text-[10px] text-blue-600 font-semibold mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to start →</p>
            </button>

            <button
              onClick={() => setMode('contacts')}
              data-testid="import-mode-contacts"
              className="group p-6 rounded-xl border-2 border-dashed border-gray-200 hover:border-green-400 hover:bg-green-50/30 transition-all text-left"
            >
              <Users className="w-8 h-8 text-green-600 mb-3" />
              <h4 className="text-sm font-bold text-gray-900">Buyers & Suppliers</h4>
              <p className="text-[11px] text-gray-500 mt-1">Import customers (buyers) and vendors (suppliers) with contact details, addresses, GSTIN</p>
              <p className="text-[10px] text-green-600 font-semibold mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to start →</p>
            </button>
          </div>

          {/* Download templates */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 mb-2 font-semibold uppercase tracking-wider">Download sorted files for review</p>
            <div className="flex gap-3">
              <a href="/Prismoline_Sorted_Item_Master.xlsx" download
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <Download className="w-3 h-3" />Item Master (Sorted)
              </a>
              <a href="/Prismoline_Sorted_Buyers_Suppliers.xlsx" download
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <Download className="w-3 h-3" />Buyers & Suppliers (Sorted)
              </a>
            </div>
          </div>
          {/* Tally Integration */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 mb-3 font-semibold uppercase tracking-wider">Tally Integration</p>
            <div className="flex items-center gap-4">
              <button
                onClick={handleImportTally}
                disabled={tallyImporting}
                data-testid="import-tally-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 disabled:opacity-50 transition-colors"
              >
                {tallyImporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Import Vendors from Tally
              </button>
              <span className="text-[10px] text-gray-400">Imports creditors from Tally Prime via ODBC. Duplicates are skipped.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isProducts = mode === 'products';
  const accentColor = isProducts ? '#24549C' : '#0D6B3E';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isProducts ? <Package className="w-5 h-5" style={{ color: accentColor }} /> : <Users className="w-5 h-5" style={{ color: accentColor }} />}
          <div>
            <h3 className="text-sm font-bold text-gray-900">{isProducts ? 'Import Item Master' : 'Import Buyers & Suppliers'}</h3>
            <p className="text-[10px] text-gray-500">Step: {step === 'upload' ? 'Upload File' : step === 'preview' ? 'Review & Confirm' : step === 'importing' ? 'Importing...' : 'Complete'}</p>
          </div>
        </div>
        <button onClick={reset} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
          <X className="w-3.5 h-3.5" />Back
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      {/* Upload Step */}
      {step === 'upload' && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3" style={{ color: accentColor }} />
          <p className="text-sm font-semibold text-gray-900 mb-1">Upload your Excel file</p>
          <p className="text-[11px] text-gray-500 mb-4">Supports .xlsx and .xls files</p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.xlsm"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="import-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            data-testid="import-upload-btn"
            className="inline-flex items-center gap-2 px-6 py-2.5 text-xs font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: accentColor }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {loading ? 'Parsing...' : 'Choose File'}
          </button>
        </div>
      )}

      {/* Preview Step */}
      {step === 'preview' && previewData && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h4 className="text-xs font-bold text-gray-900 mb-3">Import Preview</h4>

            {isProducts ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Total Items" value={previewData.total_rows} color={accentColor} />
                <StatCard label="Importable" value={previewData.importable} color="#059669" />
                <StatCard label="Duplicates (skip)" value={previewData.duplicates} color="#d97706" />
                <StatCard label="Missing HSN" value={previewData.missing_hsn} color="#ea580c" />
                <StatCard label="Warnings" value={previewData.warnings_count} color="#dc2626" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Total Records" value={previewData.total_rows} color={accentColor} />
                <StatCard label="Buyers" value={previewData.buyers} color="#2563eb" />
                <StatCard label="Suppliers" value={previewData.suppliers} color="#059669" />
                <StatCard label="Both" value={previewData.both} color="#7c3aed" />
                <StatCard label="Warnings" value={previewData.warnings_count} color="#dc2626" />
              </div>
            )}
          </div>

          {/* Warnings */}
          {previewData.warnings && previewData.warnings.length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200">
              <button onClick={() => setExpandedSection(expandedSection === 'warnings' ? null : 'warnings')}
                className="w-full flex items-center justify-between p-4">
                <span className="text-xs font-bold text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {previewData.warnings.length} Warnings (click to expand)
                </span>
                {expandedSection === 'warnings' ? <ChevronDown className="w-4 h-4 text-amber-500" /> : <ChevronRight className="w-4 h-4 text-amber-500" />}
              </button>
              {expandedSection === 'warnings' && (
                <div className="px-4 pb-4 max-h-48 overflow-y-auto">
                  {previewData.warnings.map((w, i) => (
                    <p key={i} className="text-[10px] text-amber-700 py-0.5 border-b border-amber-50 last:border-0">{w}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Data Preview Table */}
          <div className="bg-white rounded-xl border border-gray-100">
            <button onClick={() => setExpandedSection(expandedSection === 'data' ? null : 'data')}
              className="w-full flex items-center justify-between p-4">
              <span className="text-xs font-bold text-gray-900">
                Data Preview ({isProducts ? previewData.items?.length : previewData.items?.length} rows)
              </span>
              {expandedSection === 'data' ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {expandedSection === 'data' && (
              <div className="px-4 pb-4 overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {isProducts ? (
                        <>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">SKU</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Name</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Category</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Unit</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">HSN</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">GST</th>
                          <th className="text-right py-1.5 px-2 font-semibold text-gray-500">Cost</th>
                          <th className="text-right py-1.5 px-2 font-semibold text-gray-500">Sell</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Issues</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Company</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Type</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">City/State</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">GSTIN</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Salesperson</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-gray-500">Issues</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.items || []).slice(0, 50).map((item, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        {isProducts ? (
                          <>
                            <td className="py-1 px-2 font-mono">{item.sku}</td>
                            <td className="py-1 px-2">{(item.name || '').slice(0, 40)}</td>
                            <td className="py-1 px-2">{item.category}</td>
                            <td className="py-1 px-2">{item.unit}</td>
                            <td className="py-1 px-2 font-mono">{item.hsn_code}</td>
                            <td className="py-1 px-2">{item.gst_rate > 1 ? `${item.gst_rate}%` : `${(item.gst_rate * 100).toFixed(0)}%`}</td>
                            <td className="py-1 px-2 text-right">{item.cost_price > 0 ? item.cost_price.toLocaleString('en-IN') : '-'}</td>
                            <td className="py-1 px-2 text-right">{item.selling_price > 0 ? item.selling_price.toLocaleString('en-IN') : '-'}</td>
                            <td className="py-1 px-2">
                              {item.warnings?.length > 0 && (
                                <span className="text-amber-600 font-semibold">{item.warnings.join(', ')}</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-1 px-2 font-medium">{(item.company_name || '').slice(0, 30)}</td>
                            <td className="py-1 px-2">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                                item.type === 'Buyer' ? 'bg-blue-50 text-blue-700' :
                                item.type === 'Supplier' ? 'bg-green-50 text-green-700' :
                                'bg-purple-50 text-purple-700'
                              }`}>{item.type}</span>
                            </td>
                            <td className="py-1 px-2">{item.city}{item.state ? `, ${item.state}` : ''}</td>
                            <td className="py-1 px-2 font-mono">{(item.gstin || '-').slice(0, 15)}</td>
                            <td className="py-1 px-2">{item.salesperson_name || '-'}</td>
                            <td className="py-1 px-2">
                              {item.flags?.length > 0 && (
                                <span className="text-amber-600 font-semibold">{item.flags.join(', ')}</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(previewData.items || []).length > 50 && (
                  <p className="text-[10px] text-gray-400 text-center mt-2">Showing 50 of {previewData.items.length} rows...</p>
                )}
              </div>
            )}
          </div>

          {/* Delete existing + Confirm */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <label className="flex items-start gap-3 mb-4 cursor-pointer group">
              <input
                type="checkbox"
                checked={deleteFirst}
                onChange={(e) => setDeleteFirst(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
                data-testid="import-delete-existing"
              />
              <div>
                <span className="text-xs font-semibold text-red-700 group-hover:text-red-800">
                  <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                  Delete all existing {isProducts ? 'products' : 'customers & vendors'} before importing
                </span>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {isProducts
                    ? 'This will soft-delete all current products and do a fresh import from the file'
                    : 'This will soft-delete all current customers and vendors, then import fresh from the file'
                  }
                </p>
              </div>
            </label>

            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                disabled={loading}
                data-testid="import-confirm-btn"
                className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: accentColor }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {loading ? 'Importing...' : `Confirm Import${deleteFirst ? ' (fresh)' : ''}`}
              </button>
              <button
                onClick={reset}
                className="px-4 py-2.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Importing */}
      {step === 'importing' && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: accentColor }} />
          <p className="text-sm font-semibold text-gray-900">Importing data...</p>
          <p className="text-xs text-gray-500 mt-1">This may take a moment for large files</p>
        </div>
      )}

      {/* Done */}
      {step === 'done' && result && (
        <div className="bg-white rounded-xl border border-green-200 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 mb-2">Import Complete!</p>

          {isProducts ? (
            <div className="text-xs text-gray-600 space-y-1">
              <p><span className="font-semibold text-green-700">{result.imported}</span> products imported</p>
              <p><span className="font-semibold text-amber-600">{result.skipped}</span> skipped (duplicates)</p>
              {result.errors > 0 && <p><span className="font-semibold text-red-600">{result.errors}</span> errors</p>}
            </div>
          ) : (
            <div className="text-xs text-gray-600 space-y-1">
              <p><span className="font-semibold text-blue-700">{result.customers_imported}</span> customers imported</p>
              <p><span className="font-semibold text-green-700">{result.vendors_imported}</span> vendors imported</p>
              {result.errors > 0 && <p><span className="font-semibold text-red-600">{result.errors}</span> errors</p>}
            </div>
          )}

          <button
            onClick={reset}
            data-testid="import-done-btn"
            className="mt-4 px-6 py-2 text-xs font-semibold text-white rounded-lg"
            style={{ backgroundColor: accentColor }}
          >
            Done — Import More
          </button>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
    <p className="text-lg font-bold" style={{ color, fontFamily: 'DM Sans' }}>{value}</p>
    <p className="text-[10px] text-gray-500">{label}</p>
  </div>
);

export default BulkImportTab;
